﻿import { CodeEditFields, CodeEditSessionMetadata } from '@/shared/codeedit/codeEditFields.ts';
import { FieldDefinition } from '@/shared/codeedit/field.ts';
import { InputRegexValidator } from '@/shared/helpers/inputRegexValidator.ts';

export type CodeEditSessionItem = {
    uniquenessErrors: Record<string, ValidationError>;
    modified: CodeEditFields; // fields for editing
    original: CodeEditFields;
    identityConflict?: IdentityConflict;
    validationErrors: ValidationErrors;
    metadata: CodeEditSessionMetadata;
};

export type IdentityConflict = {
    message: string;
    name: string;
    id: number;
    isValid: boolean;
};

export enum ErrorType {
    Info = 'info',
    Warning = 'warning',
    Error = 'error'
}

export function BulmaErrorClass(errType?: ErrorType): string {
    switch (errType) {
        case ErrorType.Error:
            return 'is-danger';
        case ErrorType.Info:
            return 'is-info';
        case ErrorType.Warning:
            return 'is-warning';
        default:
            return '';
    }
}

export type ValidationError = {
    message: string;
    type: ErrorType;
};

export type ValidationErrors = {
    name?: ValidationError;
    description?: ValidationError;
} & Record<string, ValidationError>;

export type EditCodeSet = Record<number, CodeEditSessionItem>;

export type EditLibrary = Record<string, EditCodeSet>;

export type EditLibraries = Record<string, EditLibrary>;

export type CodeSetResult<T> = Record<string, T>;

export type LibraryResult<T> = Record<string, CodeSetResult<T>>;

export type LibrariesResult<T> = Record<string, LibraryResult<T>>;

export type ApplyToCodeFunc<T> = (code: CodeEditSessionItem, fieldDefinitions: FieldDefinition[]) => T | undefined;

export type ApplyToCodeSetFuncMetadata = {
    libraryName: string;
    codeSetName: string;
};

export type ApplyToCodeSetFunc<T> = (cs: EditCodeSet, fieldDefinitions: FieldDefinition[], metadata: ApplyToCodeSetFuncMetadata) => T | undefined;

export type ApplyToLibraryFuncMetadata = {
    libraryName: string;
};

export type ApplyToLibraryFunc<T> = (lib: EditLibrary, fieldDefinitions: FieldDefinition[], metadata: ApplyToLibraryFuncMetadata) => T | undefined;

export type ApplyToCodesResult<T> = {
    lib: string;
    cs: string;
    codeId: number;
    result: T;
};

export type ApplyToCodeSetsResult<T> = {
    lib: string;
    cs: string;
    result: T;
};

export type ApplyToLibrariesResult<T> = {
    lib: string;
    result: T;
};

export type RegexValidators = {
    name?: InputRegexValidator;
    description?: InputRegexValidator;
} & Record<string, InputRegexValidator>;
